import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

// ── Images ────────────────────────────────────────────────────────────────
import keyFeatureIcon from '../../../assets/images/tatvacare/keyfeature.webp'

// FIXED IMAGE EXTENSION (.webp instead of .png)
import laptopMockup from '../../../assets/images/tatvacare/laptopmockup.webp'

import screen1 from '../../../assets/images/tatvacare/screen1.webp'
import screen2 from '../../../assets/images/tatvacare/screen2.webp'
import screen3 from '../../../assets/images/tatvacare/screen3.webp'

const SCREENS = [screen1, screen2, screen3]

const featureData = [
  {
    number: '01',
    title: 'AI Medical Assistant',
    point: 'Ask anything, get precise answers instantly',
  },
  {
    number: '02',
    title: 'Verified Sources',
    point: 'Transparent, trustworthy medical references',
  },
  {
    number: '03',
    title: 'Smart Suggestions',
    point: 'Explore related clinical queries effortlessly',
  },
  {
    number: '04',
    title: 'Conversation History',
    point: 'Track past interactions and insights',
  },
  {
    number: '05',
    title: 'Multi-Specialty Support',
    point: 'Covers diverse medical domains',
  },
  {
    number: '06',
    title: 'Profile Management',
    point: 'Personalized experience for doctors',
  },
]

// ── TYPES ────────────────────────────────────────────────────────────────
type SlotType = 'left' | 'center' | 'right' | null

// ── HELPERS ──────────────────────────────────────────────────────────────
function getSlots(active: number, total: number) {
  const prev = (active - 1 + total) % total
  const next = (active + 1) % total

  return {
    left: prev,
    center: active,
    right: next,
  }
}

// ── SCREEN STYLES ────────────────────────────────────────────────────────
const SLOT_CONFIG = {
  left: {
    x: '-79%',
    scale: 0.9,
    zIndex: 1,
    opacity: 1,
    borderRadius: '0px',
    filter: 'brightness(1)',
    width: '50%',
  },

  center: {
    x: '0%',
    scale: 1,
    zIndex: 3,
    opacity: 1,
    borderRadius: '0px',
    filter: 'brightness(1)',
    width: '64%',
  },

  right: {
    x: '78%',
    scale: 0.9,
    zIndex: 1,
    opacity: 1,
    borderRadius: '0px',
    filter: 'brightness(1)',
    width: '50%',
  },
}

// FIXED TRANSITION TYPE
const TRANSITION = {
  duration: 0.75,
  ease: 'easeInOut' as const,
}

const KeyFeatures = () => {
  const [active, setActive] = useState<number>(0)

  // ── AUTO SLIDE ─────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      setActive(prev => (prev + 1) % SCREENS.length)
    }, 3000)

    return () => clearInterval(id)
  }, [])

  const { left, center, right } = getSlots(active, SCREENS.length)

  const slotOf = (idx: number): SlotType => {
    if (idx === left) return 'left'
    if (idx === center) return 'center'
    if (idx === right) return 'right'

    return null
  }

  return (
    <section className="w-full bg-purple/20 py-5 md:py-20">
      <div className="mx-auto w-full max-w-[92%]">

        {/* ── HEADING ───────────────────────────────────────────── */}
        <div className="flex items-center justify-center gap-3">
          <img
            src={keyFeatureIcon}
            alt="Key Features icon"
            className="h-8 w-8 md:h-10 md:w-10 lg:h-12 lg:w-12"
          />

          <h2 className="text-center font-gilroy-bold text-3xl text-white md:text-4xl lg:text-5xl">
            Key Features
          </h2>
        </div>

        {/* ── LAPTOP ────────────────────────────────────────────── */}
        <div className="relative mx-auto mt-10 w-[85%] select-none">

          <img
            src={laptopMockup}
            alt="Laptop mockup"
            className="relative z-10 w-full pointer-events-none"
            draggable={false}
          />

          {/* ── SCREEN AREA ───────────────────────────────────── */}
          <div
            className="pointer-events-none absolute inset-0 flex items-start justify-center"
            style={{ zIndex: 5 }}
          >
            <div
              className="relative flex items-center justify-center"
              style={{
                position: 'absolute',
                top: '4.9%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '77.5%',
                height: '69.5%',
              }}
            >
              {SCREENS.map((src, idx) => {
                const slot = slotOf(idx)

                if (!slot) return null

                const cfg = SLOT_CONFIG[slot]

                return (
                  <motion.div
                    key={idx}
                    animate={{
                      x: cfg.x,
                      scale: cfg.scale,
                      opacity: cfg.opacity,
                      zIndex: cfg.zIndex,
                    }}
                    transition={TRANSITION}
                    style={{
                      position: 'absolute',
                      width: cfg.width,
                      height: '100%',
                      borderRadius: cfg.borderRadius,
                      overflow: 'hidden',
                      filter: cfg.filter,
                      pointerEvents:
                        slot === 'center' ? 'auto' : 'none',
                      boxShadow:
                        slot === 'center'
                          ? '0px 15px 50px rgba(0,0,0,0.35)'
                          : '0px 10px 30px rgba(0,0,0,0.15)',
                    }}
                    onClick={() => {
                      if (slot === 'left') {
                        setActive(
                          (active - 1 + SCREENS.length) %
                            SCREENS.length
                        )
                      }

                      if (slot === 'right') {
                        setActive(
                          (active + 1) % SCREENS.length
                        )
                      }
                    }}
                  >
                    <img
                      src={src}
                      alt={`screen-${idx}`}
                      className="h-full w-full object-cover object-top"
                      draggable={false}
                    />
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>

        {/* ── DOTS ─────────────────────────────────────────────── */}
        <div className="mt-4 flex justify-center gap-2">
          {SCREENS.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className="transition-all duration-300"
              style={{
                width: i === active ? 24 : 8,
                height: 8,
                borderRadius: 0,
                background:
                  i === active
                    ? '#ffffff'
                    : 'rgba(255,255,255,0.35)',
              }}
            />
          ))}
        </div>

        {/* ── FEATURE CARDS ───────────────────────────────────── */}
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">

          {featureData.map((item, index) => {
            const isLeft = index % 2 === 0

            return (
              <motion.div
                key={item.number}
                initial={{
                  opacity: 0,
                  x: isLeft ? -70 : 70,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                viewport={{
                  once: false,
                  amount: 0.25,
                }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.08,
                  ease: 'easeOut',
                }}
                className="rounded-[20px] bg-[#ffffff10] px-3 py-4 backdrop-blur md:px-6 md:py-8"
              >
                <div className="flex h-[50px] w-[50px] items-center justify-center rounded-full bg-white md:h-[60px] md:w-[60px]">
                  <span className="font-gilroy-bold text-xl text-black">
                    {item.number}
                  </span>
                </div>

                <h3 className="mt-3 font-gilroy-bold text-lg text-white md:text-2xl">
                  {item.title}
                </h3>

                <p className="mt-1 text-sm text-white/70 md:text-lg">
                  {item.point}
                </p>
              </motion.div>
            )
          })}

        </div>
      </div>
    </section>
  )
}

export default KeyFeatures